import React, { useMemo } from 'react';
import {
  alpha,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TableHeaderCell } from 'components/common/TableHeaderCell';

import { ListSort } from '@hooks';
import { OrderDirection } from '@constants';
import { IListColumn, resolveColumns } from '../columns';
import { GroupedRows } from '../grouping';
import { CandidateGroupHeader } from './CandidateGroupHeader';

export const useResolvedColumns = <Column extends IListColumn<string, any>>(
  columns: Column[],
  selected: string[],
) => {
  const theme = useTheme();
  const isLaptop = useMediaQuery(theme.breakpoints.up('lg'));
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  const resolved = useMemo(
    () =>
      resolveColumns(
        columns,
        selected,
        isLaptop ? 'lg' : isWide ? 'md' : 'tablet',
      ),
    [columns, selected, isLaptop, isWide],
  );

  return { columns: resolved, isLaptop };
};

interface IGroupedTableProps<T, Column> {
  groups: GroupedRows<T>[];
  columns: Column[];
  sort: ListSort;
  onSort: (key: string) => void;
  onRowClick: (id: string) => void;
  renderCell: (column: Column, row: T) => React.ReactNode;
  isHighlighted?: (row: T) => boolean;
}

export const GroupedTable = <
  T extends { id: string },
  Column extends IListColumn<string, any>,
>({
  groups,
  columns,
  sort,
  onSort,
  onRowClick,
  renderCell,
  isHighlighted = () => false,
}: IGroupedTableProps<T, Column>) => (
  <Table sx={{ tableLayout: 'fixed', width: '100%', backgroundColor: 'white' }}>
    <TableHead>
      <TableRow sx={{ '& > .MuiTableCell-root': { border: 'none', py: 1 } }}>
        {columns.map((column) => (
          <TableHeaderCell
            key={column.id}
            label={column.label}
            sortKey={column.sortKey}
            activeSortKey={sort.key}
            sortDir={sort.direction === OrderDirection.DESC ? 'desc' : 'asc'}
            onSort={column.sortKey ? onSort : undefined}
            width={column.width}
            align={column.align}
            sticky
            stickyOffset={-16}
          />
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      {groups.map((group, index) => (
        <React.Fragment key={index}>
          {Boolean(group.section) &&
            group.section !== groups[index - 1]?.section && (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={(theme) => ({
                    padding: '16px 16px 6px',
                    borderTop: 'none',
                    borderBottom: `1px solid ${theme.palette.background.divider}`,
                  })}
                >
                  <Typography variant="h3" noWrap>
                    {group.section}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          {group.name && (
            <TableRow
              sx={{
                backgroundColor: (theme) => theme.palette.highlight.neutral,
                '& > .MuiTableCell-root': { borderBottom: 'none' },
              }}
            >
              <TableCell
                colSpan={columns.length}
                sx={{
                  padding: '6px 16px',
                  borderTop: 'none',
                  borderBottom: 'none',
                }}
              >
                <CandidateGroupHeader group={group} />
              </TableCell>
            </TableRow>
          )}
          {group.candidates.map((row, rowIndex) => (
            <TableRow
              key={row.id}
              onClick={() => onRowClick(row.id)}
              sx={(theme) => ({
                cursor: 'pointer',
                height: 48,
                backgroundColor: isHighlighted(row)
                  ? theme.palette.background.brandLight
                  : undefined,
                '& > .MuiTableCell-root': {
                  paddingTop: 0.5,
                  paddingBottom: 0.5,
                  borderBottom:
                    rowIndex === group.candidates.length - 1 &&
                    index < groups.length - 1
                      ? 'none'
                      : undefined,
                },
                '&:hover': {
                  background: isHighlighted(row)
                    ? alpha(theme.palette.background.brandLight, 0.7)
                    : theme.palette.highlight.actionable,
                },
              })}
            >
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align}>
                  {renderCell(column, row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </React.Fragment>
      ))}
    </TableBody>
  </Table>
);
